/**
 * Mode dégradé (CP-12) — ce qu'un poste d'accueil garde en cache pour vérifier
 * une éligibilité sans réseau.
 *
 * **Données administratives uniquement** : de quoi reconnaître une personne et
 * lire le verdict de sa dernière synchronisation. Jamais rien de médical
 * (RG-PA-07) — ce schéma est la liste fermée de ce qui a le droit de dormir
 * sur le disque d'un poste partagé.
 */
import { z } from 'zod';
import { TYPES_BENEFICIAIRE } from '../rules/beneficiaire.js';
import { AGE_LIMITE_ENFANT, MOTIFS_NON_ELIGIBLE, STATUTS_ADHERENT, } from '../rules/eligibilite.js';
/**
 * De quoi recalculer le verdict au jour de la consultation (S74, D2) : des
 * drapeaux et un statut d'entreprise, rien de plus. `null` pour un client
 * externe ou une fiche sans contrat, dont le verdict ne dépend pas du jour.
 */
export const schemaConditionsHorsLigne = z.object({
    presentDansDernierDns: z.boolean(),
    statutAdherent: z.enum(STATUTS_ADHERENT),
    titulaireEligible: z.boolean().nullable(),
    dispenseDeVerificationDns: z.boolean(),
    enAttenteDeConfirmation: z.boolean(),
    retireDeLaCouverture: z.boolean(),
    partiALaMain: z.boolean(),
    /**
     * Décision 338 — la limite d'âge des enfants de l'entreprise. Un coffre
     * scellé avant elle n'en a pas : c'était 21 ans pour toutes.
     */
    ageLimiteEnfant: z.number().int().default(AGE_LIMITE_ENFANT),
});
export const schemaPersonneHorsLigne = z.object({
    id: z.uuid(),
    type: z.enum(TYPES_BENEFICIAIRE),
    nomComplet: z.string(),
    matricule: z.string().nullable(),
    /** `AAAA-MM-JJ` — elle départage deux homonymes quand on n'a que le papier sous les yeux. */
    dateNaissance: z.string().nullable(),
    entreprise: z.string().nullable(),
    entrepriseCode: z.string().nullable(),
    offre: z.string().nullable(),
    /** Pour un ayant droit : le salarié auquel il se rattache. */
    titulaire: z.string().nullable(),
    /** Le verdict AU MOMENT de l'export — c'est pour cela que la fraîcheur s'affiche toujours. */
    eligible: z.boolean(),
    motif: z.enum(MOTIFS_NON_ELIGIBLE).nullable(),
    /**
     * Facultatifs : un coffre scellé avant leur arrivée s'ouvre encore, et son
     * verdict reste celui de la synchronisation.
     */
    conditions: schemaConditionsHorsLigne.nullable().optional(),
    /** Un écart d'offre ouvert, sur la fiche ou sur son titulaire : l'entrée attend la chargée (S74, D3). */
    ecartOffre: z.boolean().optional(),
});
export const schemaReferentielHorsLigne = z.object({
    /** L'instant où les verdicts ont été calculés : « Données du 12/03 à 08:15 ». */
    genereLe: z.iso.datetime(),
    personnes: z.array(schemaPersonneHorsLigne),
});
//# sourceMappingURL=hors-ligne.js.map